import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  deleteUserAccount,
  exportUserData,
  updateProfile,
  useCurrentUserProfile,
} from '@busy/api';
import { ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import { useAuth } from '../../store/auth';
import { useT } from '../../lib/i18n';

// Settings → Privacy & data. Two actions:
//   1. Export data — bundles every user-owned row across every table into a
//      JSON file the browser downloads. Photos are referenced by storage_path,
//      not embedded.
//   2. Delete account — permanent. Purges photo blobs from storage, then RPC
//      deletes auth.users which cascades through every table.
//
// Required for App Store / GDPR compliance.

export function PrivacyDataPanel() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const profile = useCurrentUserProfile();
  const signOut = useAuth((s) => s.signOut);

  const [exporting, setExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savingMask, setSavingMask] = useState(false);

  const sharesName = profile.data?.share_name_on_leaderboard ?? true;
  async function toggleNameMask() {
    setSavingMask(true);
    const { error } = await updateProfile({ share_name_on_leaderboard: !sharesName });
    setSavingMask(false);
    if (error) {
      toast(t('settings.privacy.maskError', { message: error.message }), 'error');
    } else {
      toast(
        sharesName
          ? t('settings.privacy.maskOnToast')
          : t('settings.privacy.maskOffToast'),
        'success',
      );
    }
  }

  async function handleExport() {
    setExporting(true);
    const { data, error } = await exportUserData();
    setExporting(false);
    if (error || !data) {
      toast(t('settings.privacy.exportError', { message: error?.message ?? '' }), 'error');
      return;
    }

    const stamp = new Date().toISOString().slice(0, 10);
    const filename = `busybarber-export-${stamp}.json`;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast(t('settings.privacy.exportSuccess'), 'success');
  }

  return (
    <div className="space-y-4">
      {/* Account info */}
      <div className="rounded-2xl border border-line bg-bg-alt p-4">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-500 mb-1">
          {t('settings.privacy.accountFor')}
        </div>
        <div className="font-medium break-all text-sm">{profile.data?.email}</div>
      </div>

      {/* Export */}
      <section className="rounded-2xl border border-line bg-surface p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-navy-900/8 dark:bg-gold-500/15 grid place-items-center shrink-0">
            <DownloadIcon className="w-5 h-5 text-navy-900 dark:text-gold-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[15px]">{t('settings.privacy.exportTitle')}</h3>
            <p className="text-xs text-ink-500 leading-relaxed mt-0.5">
              {t('settings.privacy.exportSub')}
            </p>
          </div>
        </div>

        <ul className="text-[12px] text-ink-500 space-y-1 pl-12">
          <li>{t('settings.privacy.bullet.profile')}</li>
          <li>{t('settings.privacy.bullet.clients')}</li>
          <li>{t('settings.privacy.bullet.visits')}</li>
          <li>{t('settings.privacy.bullet.lessons')}</li>
          <li>{t('settings.privacy.bullet.achievements')}</li>
        </ul>

        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || profile.loading}
          className="w-full h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
        >
          {exporting ? t('settings.privacy.exporting') : t('settings.privacy.exportButton')}
        </button>
      </section>

      {/* Leaderboard visibility — masking only swaps the rendered name for
          "Hidden barber" on other users' boards. Rank, nickname, and badge
          counts stay visible (it's the competition signal). The viewer
          always sees their own name regardless of this flag. */}
      <section className="rounded-2xl border border-line bg-surface p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-navy-900/8 dark:bg-gold-500/15 grid place-items-center shrink-0">
            <EyeIcon className="w-5 h-5 text-navy-900 dark:text-gold-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[15px]">{t('settings.privacy.maskTitle')}</h3>
            <p className="text-xs text-ink-500 leading-relaxed mt-0.5">
              {t('settings.privacy.maskSub')}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={toggleNameMask}
          disabled={savingMask || profile.loading}
          aria-pressed={sharesName}
          className={`w-full h-11 rounded-lg font-semibold text-sm flex items-center justify-between px-4 border transition-colors disabled:opacity-50 ${
            sharesName
              ? 'border-line bg-bg-alt text-ink-700'
              : 'border-navy-900 bg-navy-900 text-white dark:border-gold-500 dark:bg-gold-500 dark:text-navy-900'
          }`}
        >
          <span>
            {sharesName
              ? t('settings.privacy.maskShowing')
              : t('settings.privacy.maskHidden')}
          </span>
          <span
            className={`w-9 h-5 rounded-full relative transition-colors ${
              sharesName ? 'bg-line' : 'bg-white/30'
            }`}
            aria-hidden
          >
            <span
              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${
                sharesName ? 'left-0.5' : 'left-[18px]'
              }`}
            />
          </span>
        </button>
      </section>

      {/* Delete account */}
      <section className="rounded-2xl border border-red-300 dark:border-red-500/40 bg-red-50/60 dark:bg-red-500/10 p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-500/15 dark:bg-red-500/25 grid place-items-center shrink-0">
            <TrashIcon className="w-5 h-5 text-red-600 dark:text-red-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-[15px] text-red-900 dark:text-red-100">
              {t('settings.privacy.deleteTitle')}
            </h3>
            <p className="text-xs text-red-900/80 dark:text-red-100/80 leading-relaxed mt-0.5">
              {t('settings.privacy.deleteSub')}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          className="w-full h-11 rounded-lg border border-red-500/60 bg-surface text-red-600 dark:text-red-400 font-semibold hover:bg-red-50 dark:hover:bg-red-600/15"
        >
          {t('settings.privacy.deleteButton')}
        </button>
      </section>

      {confirmOpen && (
        <DeleteConfirmModal
          email={profile.data?.email ?? ''}
          onClose={() => setConfirmOpen(false)}
          onConfirmed={async () => {
            await signOut();
            navigate('/', { replace: true });
            toast(t('settings.privacy.deletedToast'), 'success');
          }}
        />
      )}
    </div>
  );
}

function DeleteConfirmModal({
  email,
  onClose,
  onConfirmed,
}: {
  email: string;
  onClose: () => void;
  onConfirmed: () => void | Promise<void>;
}) {
  const t = useT();
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const expected = 'DELETE';
  const canConfirm = typed.trim().toUpperCase() === expected;

  async function handleConfirm() {
    if (!canConfirm) return;
    setBusy(true);
    const { error } = await deleteUserAccount();
    setBusy(false);
    if (error) {
      toast(t('settings.privacy.deleteError', { message: error.message }), 'error');
      return;
    }
    await onConfirmed();
  }

  return (
    <ModalShell open onClose={busy ? () => undefined : onClose} size="sm" ariaLabel={t('settings.privacy.deleteAria')}>
      <div className="bg-red-600 text-white px-6 pt-7 pb-5 rounded-t-2xl relative">
        {!busy && <ModalCloseButton onClose={onClose} variant="dark" />}
        <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-white/80 mb-1.5">
          {t('settings.privacy.deleteEyebrow')}
        </div>
        <h2 className="font-display text-2xl leading-tight">{t('settings.privacy.deleteModalTitle')}</h2>
        <p className="text-sm text-white/85 mt-2 leading-relaxed">
          {t('settings.privacy.deleteModalSub', { email })}
        </p>
      </div>

      <div className="p-6 space-y-4">
        <ul className="text-[13px] text-ink-700 space-y-1.5 list-disc pl-5">
          <li>{t('settings.privacy.deleteWipe.clients')}</li>
          <li>{t('settings.privacy.deleteWipe.visits')}</li>
          <li>{t('settings.privacy.deleteWipe.photos')}</li>
          <li>{t('settings.privacy.deleteWipe.streaks')}</li>
          <li>{t('settings.privacy.deleteWipe.account')}</li>
        </ul>

        <div>
          <label className="block text-xs font-bold text-ink-500 mb-1.5">
            {t('settings.privacy.typeToConfirm', { word: expected })}
          </label>
          <input
            type="text"
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={expected}
            className="w-full h-11 px-3 rounded-lg border border-line bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex-1 h-11 rounded-lg border border-line bg-surface font-semibold disabled:opacity-50"
          >
            {t('settings.privacy.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm || busy}
            className="flex-1 h-11 rounded-lg bg-red-600 text-white font-semibold disabled:opacity-40"
          >
            {busy ? t('settings.privacy.deleting') : t('settings.privacy.deleteForever')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
